import {
  ApiResponseError,
  ApiSessionTimeoutError,
  classifyApiError,
  unwrapResponseModel
} from '../api/responseModel';
import { serializeDateForApi } from '../dates/dateService';

export {
  ApiResponseError as ActasApiError,
  ApiSessionTimeoutError as ActasSessionTimeoutError,
  classifyApiError as classifyActasError,
  unwrapResponseModel
};

export function toSaveActaCursadaPayload(resultados) {
  return resultados.map((tag) => ({
    DetalleActaCursadaCod: tag.id,
    DetalleActaCursadaResultado: tag.resultado
  }));
}

export function normalizeActaExamenTipoCode(tipo) {
  const value = String(tipo || '').trim().toLowerCase();
  if (!value) {
    return '';
  }
  if (value === 'r' || value.startsWith('regular')) {
    return 'R';
  }
  if (value === 'l' || value.startsWith('libre')) {
    return 'L';
  }
  if (value === 'e' || value.startsWith('equival')) {
    return 'E';
  }
  if (value === 'p' || value.startsWith('promoc')) {
    return 'P';
  }
  return String(tipo || '').trim().toUpperCase();
}

export function isActaExamenPromocion(tipo) {
  return normalizeActaExamenTipoCode(tipo) === 'P';
}

export function isActaExamenEquivalencia(tipo) {
  return normalizeActaExamenTipoCode(tipo) === 'E';
}

export function isActaExamenFechaActaEditable(tipo) {
  return normalizeActaExamenTipoCode(tipo) === 'P';
}

export function parseActaExamenFolio(folio) {
  const folioTrim = String(folio ?? '').trim();
  if (!folioTrim) {
    return null;
  }

  const parsed = Number(folioTrim);
  if (!Number.isFinite(parsed) || !Number.isInteger(parsed) || parsed < 0) {
    return null;
  }

  return parsed;
}

export function isValidActaExamenFolio(folio) {
  return parseActaExamenFolio(folio) !== null;
}

export function toSaveActaExamenNotasPayload({ folio, libro, actaId, resultados, tipoActa, fechaPromocion }) {
  const payload = {
    ActaExamenFolio: parseActaExamenFolio(folio),
    ActaExamenLibro: libro,
    ActaExamenNacta: actaId,
    Resultados: resultados.map((tag) => ({
      DetalleActaCod: tag.id,
      DetalleActaNota: tag.nota,
      DetalleActaOral: tag.oral,
      DetalleActaEscrito: tag.escrito
    }))
  };

  if (isActaExamenFechaActaEditable(tipoActa)) {
    payload.FechaActaPromocion = serializeDateForApi(fechaPromocion);
  }

  return payload;
}

export function toSaveActaExamenEncabezadoPayload({ folio, libro, actaId, tipoActa, fechaPromocion }) {
  const payload = {
    ActaExamenFolio: parseActaExamenFolio(folio),
    ActaExamenLibro: libro,
    ActaExamenNacta: actaId
  };

  if (isActaExamenFechaActaEditable(tipoActa)) {
    payload.FechaActaPromocion = serializeDateForApi(fechaPromocion);
  }

  return payload;
}

export function buildConsultarActasParams({
  fechaDesde,
  fechaHasta,
  folio,
  libro,
  tipoActa,
  materiaCarreraCod
}) {
  const params = {};
  if (fechaDesde) params.fechaDesde = fechaDesde;
  if (fechaHasta) params.fechaHasta = fechaHasta;
  if (isValidActaExamenFolio(folio)) params.folio = parseActaExamenFolio(folio);
  if (libro) params.libro = libro;
  if (materiaCarreraCod) params.materiaCarreraCod = materiaCarreraCod;
  if (tipoActa) params.tipoActa = tipoActa;
  return params;
}

export function validateEncabezadoForm(libro, folio, { tipoActa, fechaPromocion, actaCargada } = {}) {
  const libroTrim = String(libro ?? '').trim();
  if (!libroTrim || !isValidActaExamenFolio(folio)) {
    return false;
  }
  if (isActaExamenFechaActaEditable(tipoActa) && actaCargada && !fechaPromocion) {
    return false;
  }
  return true;
}

export function validateActaCursadaResultados(alumnos, resultados) {
  return findAlumnosSinResultadoCargado(alumnos, resultados).length === 0;
}

export function findAlumnosSinResultadoCargado(alumnos, resultados) {
  return alumnos.filter((alumno) => {
    const entry = resultados.find((r) => r.id === alumno.detalleActaCursadaCod);
    return !entry || String(entry.resultado ?? '').trim() === '';
  });
}

export function validateCargarActaExamenEncabezado(folio, libro, { tipoActa, fechaPromocion } = {}) {
  const libroTrim = String(libro ?? '').trim();
  if (!libroTrim || !isValidActaExamenFolio(folio)) {
    return false;
  }
  if (isActaExamenFechaActaEditable(tipoActa) && !fechaPromocion) {
    return false;
  }
  return true;
}

export function hasActaExamenNotaPromedioCargada(entry) {
  if (!entry) {
    return false;
  }
  return String(entry.nota ?? '').trim() !== '';
}

export function findAlumnosSinNotaPromedioCargado(alumnos, resultados) {
  return alumnos.filter((alumno) => {
    const entry = resultados.find((r) => r.id === alumno.detalleActaCod);
    return !hasActaExamenNotaPromedioCargada(entry);
  });
}

export function validateActaExamenNotasPromedio(alumnos, resultados) {
  if (!alumnos.length) {
    return false;
  }
  return findAlumnosSinNotaPromedioCargado(alumnos, resultados).length === 0;
}

export function buildActaExamenResultadosForSave(alumnos, resultados) {
  return alumnos.map((alumno) => {
    const entry = resultados.find((r) => r.id === alumno.detalleActaCod);
    return {
      id: alumno.detalleActaCod,
      oral: String(entry?.oral ?? '').trim() || '0',
      escrito: String(entry?.escrito ?? '').trim() || '0',
      nota: String(entry?.nota ?? '').trim() || '0'
    };
  });
}

export function seedActaExamenResultadosFromDetalle(rows) {
  return rows.reduce((acc, row) => {
    if (!isActaExamenResultadoCargado(row.detalleActaResultado)) {
      return acc;
    }
    acc.push({
      id: row.detalleActaCod,
      oral: String(row.detalleActaOral ?? '0'),
      escrito: String(row.detalleActaEscrito ?? '0'),
      nota: String(row.detalleActaNota ?? '0')
    });
    return acc;
  }, []);
}

export function createPdfBlobFromBase64(base64) {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: 'application/pdf' });
}

export function downloadPdfBlob(blob, filename) {
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = filename;
  link.click();
}

export function downloadPdfFromBase64(base64, filename) {
  downloadPdfBlob(createPdfBlobFromBase64(base64), filename);
}

export function formatActaCursadaResultadoDisplay(resultado) {
  const code = String(resultado ?? '').trim();
  if (!code) {
    return 'Sin resultados';
  }
  if (code === 'R') return 'Regular';
  if (code === 'L') return 'Libre';
  if (code === 'P') return 'Promoción';
  if (code === 'E') return 'Equivalencia';
  return code;
}

export function formatActaExamenEstado(estado) {
  const trimmed = String(estado || '').trim();
  if (trimmed === 'ISN') return 'Impresa';
  return trimmed;
}

export function formatActaExamenTipo(tipo) {
  const value = (tipo || '').trim();
  if (value === 'R') return 'Regular';
  if (value === 'L') return 'Libre';
  if (value === 'E') return 'Equivalencia';
  if (value === 'P') return 'Promoción';
  return value;
}

export function formatActaExamenNota(nota) {
  const value = Number(nota);
  if (value === 0) {
    return 'Ausente';
  }
  if (value >= 1 && value <= 10) {
    const words = ['uno', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho', 'nueve', 'diez'];
    return `${value} (${words[value - 1]})`;
  }
  return '';
}

export function isActaExamenResultadoCargado(resultado) {
  return String(resultado ?? '').trim() !== '';
}

export function formatActaExamenResultadoDisplay(resultado) {
  const raw = String(resultado ?? '').trim();
  if (!raw) {
    return 'Sin resultados';
  }

  const normalized = raw.toLowerCase();
  if (normalized === 'ausente' || normalized === 'a') {
    return 'Ausente';
  }
  if (normalized === 'aprobado' || normalized === 'aprobó' || normalized === 'ap' || normalized === 'r') {
    return 'Aprobado';
  }
  if (normalized === 'reprobado' || normalized === 'reprobó' || normalized === 'rp') {
    return 'Reprobado';
  }

  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
}

export function formatActaExamenNotaDisplay(nota, resultado) {
  if (nota === null || nota === undefined || nota === '') {
    return '-';
  }

  const value = Number(nota);
  if (Number.isNaN(value)) {
    return '-';
  }

  if (value === 0) {
    return isActaExamenResultadoCargado(resultado) ? 'Ausente' : '-';
  }

  if (value >= 1 && value <= 10) {
    return String(value);
  }

  return '-';
}

export function formatActaExamenEncabezadoDisplay(value) {
  const raw = String(value ?? '').trim();
  if (!raw) {
    return '-';
  }
  return raw;
}

export function buildActaExamenMateriaDisplay(row) {
  if (!row) {
    return '';
  }
  return `${String(row.materiaCarreraNombre || '').trim()} - ${String(row.carreraNombre || '').trim()}`;
}

export function formatActaExamenPeriodoGrid(row) {
  return [
    (row.periodoExamenDescripcion || '').trim(),
    (row.periodoExamenLlamado || '').trim(),
    (row.anioAcademicoDescripcion || '').trim(),
  ].filter(Boolean).join(' - ');
}
